/**
 * LeftShield Portal - PR Bot Page
 * Manage merge gate policies, webhooks, and view PR-triggered scan results
 * Route: /pr-bot | Nav: Code > PR Bot
 *
 * The Webhooks tab is migrated onto the List & Grid Standard local primitives — see
 * `src/pages/ApiKeysPage.tsx` (reference migration) and
 * `.specify/journals/list-grid-adoption-journal.md`. The Pull Requests and Merge Gate
 * Policies tabs are out of scope for this migration and remain on Fluent `DataGrid`.
 */
import React, { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Body1,
  Body2,
  Card,
  Badge,
  tokens,
  Spinner,
  Button,
  Switch,
  Tab,
  TabList,
  SelectTabEvent,
  SelectTabData,
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  TableColumnDefinition,
  createTableColumn,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Input,
  ProgressBar,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
} from '@fluentui/react-components';
import {
  BranchRegular,
  ShieldCheckmarkRegular,
  PlugConnectedRegular,
  AddRegular,
  DeleteRegular,
  SaveRegular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mergeGateApi, runsApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import { useAuth } from '../features/authentication';
import {
  ListPageLayout,
  MasterDetailHeader,
  ListToolbar,
  Pager,
  actionsColumnWidth,
  useListUrlState,
  useAdaptiveGridData,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { Crumb, ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';
import type { Run, MergeGatePolicy, UpdateMergeGatePolicyRequest, WebhookRegistration } from '../types';

type TabValue = 'pullRequests' | 'policies' | 'webhooks';

const getRunStatusColor = (status: string): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (status) {
    case 'Completed':
      return 'success';
    case 'Failed':
      return 'danger';
    case 'Running':
    case 'Pending':
      return 'informative';
    case 'Cancelled':
      return 'warning';
    default:
      return 'informative';
  }
};

/** Formats delivery stats as e.g. "12 (2 failed)" — omits the failed-count suffix when zero. */
const formatDeliveries = (totalDeliveries: number, failedDeliveries: number): string =>
  failedDeliveries > 0 ? `${totalDeliveries} (${failedDeliveries} failed)` : `${totalDeliveries}`;

const getScoreColor = (score: number): string => {
  if (score >= 90) return tokens.colorPaletteGreenForeground1;
  if (score >= 70) return tokens.colorPaletteYellowForeground1;
  if (score >= 50) return tokens.colorPaletteRedForeground2;
  return tokens.colorPaletteRedForeground1;
};

/** Roles allowed to register/delete webhooks — mirrors `MergeGateController.RegisterWebhook` /
 *  `DeleteWebhook`'s `RequireAnyRole("Organization.Admin", "TenantAdmin", "IntegrationAdmin")`. */
const WEBHOOK_MANAGER_ROLES = ['Organization.Admin', 'TenantAdmin', 'IntegrationAdmin'];

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    gap: tokens.spacingVerticalL,
  },
  card: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  webhooksPanel: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: tokens.spacingHorizontalL,
  },
  monoText: {
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  eventsCell: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
  },
  actionsCell: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
  },
});

/** Wraps the (unpaged) `mergeGateApi.listWebhooks` response as a `PagedResult` — `useAdaptiveGridData`
 *  then applies search/sort/paging in memory (client mode), since a tenant's webhook registrations
 *  are never large enough to warrant a real server-paged endpoint. */
async function fetchWebhooksPage(params: ListQueryParams): Promise<PagedResult<WebhookRegistration>> {
  const items = await mergeGateApi.listWebhooks();
  return {
    items,
    totalCount: items.length,
    pageNumber: 1,
    pageSize: params.pageSize,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
  };
}

const webhookCrumbs: Crumb[] = [{ label: 'Webhooks' }];

// Local state for inline-editing policies
interface PolicyEditState {
  [repositoryId: string]: UpdateMergeGatePolicyRequest;
}

export const PRBotPage: React.FC = () => {
  const ps = usePageStyles();
  const styles = useStyles();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canManageWebhooks = user?.roles?.some((role) => WEBHOOK_MANAGER_ROLES.includes(role)) ?? false;
  const [selectedTab, setSelectedTab] = useState<TabValue>('pullRequests');
  const [policyEdits, setPolicyEdits] = useState<PolicyEditState>({});
  const [savingPolicyId, setSavingPolicyId] = useState<string | null>(null);

  // Webhook registration dialog state
  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);
  const [newWebhookName, setNewWebhookName] = useState('');
  const [newWebhookUrl, setNewWebhookUrl] = useState('');
  const [newWebhookEventTypes, setNewWebhookEventTypes] = useState('');
  const [newWebhookSecret, setNewWebhookSecret] = useState('');
  // One-time secret surfaced after a successful create — the API never returns it again.
  const [createdWebhookSecret, setCreatedWebhookSecret] = useState<string | null>(null);

  // --- Queries ---

  const { data: allRuns, isLoading: runsLoading } = useQuery({
    queryKey: ['recentRuns', 50],
    queryFn: () => runsApi.getRecent(50),
    enabled: selectedTab === 'pullRequests',
  });

  const prRuns = allRuns?.filter((r) => r.trigger === 'PullRequest') ?? [];

  const { data: policies, isLoading: policiesLoading } = useQuery({
    queryKey: ['mergeGatePolicies'],
    queryFn: () => mergeGateApi.getPolicies(),
    enabled: selectedTab === 'policies',
  });

  // Webhooks list — List & Grid Standard (§9 pagination, §19 URL state, §10 adaptive loading).
  const { state: webhookListState, setPage: setWebhookPage, setSort: setWebhookSort, setSearch: setWebhookSearch } =
    useListUrlState({ defaultSort: 'createdAt:desc' });

  const [webhookSortField, webhookSortDirRaw] = webhookListState.sort!.split(':');
  const webhookSortDir: SortDirection = webhookSortDirRaw === 'desc' ? 'desc' : 'asc';
  const webhookSortState: SortState[] = [{ field: webhookSortField, dir: webhookSortDir }];

  const toggleWebhookSort = (field: string) => {
    if (field === webhookSortField) {
      setWebhookSort(`${field}:${webhookSortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setWebhookSort(`${field}:asc`);
    }
  };

  const webhookSortIcon = (field: string) => {
    if (field !== webhookSortField) return <ArrowSort16Regular />;
    return webhookSortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const webhooksGrid = useAdaptiveGridData<WebhookRegistration>({
    queryKeyBase: ['mergeGateWebhooks'],
    fetchPage: fetchWebhooksPage,
    page: webhookListState.page,
    pageSize: webhookListState.pageSize,
    search: webhookListState.search,
    searchFields: ['name', 'url'],
    sortState: webhookSortState,
    enabled: selectedTab === 'webhooks',
  });

  const {
    items: webhookItems,
    totalCount: webhookTotalCount,
    totalPages: webhookTotalPages,
    hasPreviousPage: webhookHasPreviousPage,
    hasNextPage: webhookHasNextPage,
    isLoading: webhooksIsLoading,
    isFetching: webhooksIsFetching,
    isError: webhooksIsError,
    refetch: refetchWebhooks,
  } = webhooksGrid;

  // Out-of-range clamp (§9) — never strand the user on an empty page after delete/search.
  useEffect(() => {
    if (selectedTab === 'webhooks' && !webhooksIsLoading && webhookListState.page > webhookTotalPages) {
      setWebhookPage(webhookTotalPages);
    }
  }, [selectedTab, webhooksIsLoading, webhookListState.page, webhookTotalPages, setWebhookPage]);

  // --- Mutations ---

  const updatePolicyMutation = useMutation({
    mutationFn: ({ repositoryId, request }: { repositoryId: string; request: UpdateMergeGatePolicyRequest }) =>
      mergeGateApi.updatePolicy(repositoryId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mergeGatePolicies'] });
      setSavingPolicyId(null);
    },
    onError: () => {
      setSavingPolicyId(null);
    },
  });

  const registerWebhookMutation = useMutation({
    mutationFn: (request: { name: string; url: string; secret?: string; eventTypes?: string[] }) =>
      mergeGateApi.registerWebhook(request),
    onSuccess: (webhook) => {
      queryClient.invalidateQueries({ queryKey: ['mergeGateWebhooks'] });
      // Surface the one-time secret instead of closing the dialog — it can never be retrieved again.
      setCreatedWebhookSecret(webhook.secret ?? null);
    },
  });

  const deleteWebhookMutation = useMutation({
    mutationFn: (id: number) => mergeGateApi.deleteWebhook(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mergeGateWebhooks'] });
    },
  });

  // --- Handlers ---

  const handleTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as TabValue);
  };

  const getPolicyEdit = (policy: MergeGatePolicy): UpdateMergeGatePolicyRequest => {
    return policyEdits[policy.repositoryId] ?? {
      blockOnCritical: policy.blockOnCritical,
      blockOnHigh: policy.blockOnHigh,
      requireEvidence: policy.requireEvidence,
      minimumScore: policy.minimumScore,
      isEnabled: policy.isEnabled,
    };
  };

  const updatePolicyEdit = (repositoryId: string, partial: Partial<UpdateMergeGatePolicyRequest>, policy: MergeGatePolicy) => {
    setPolicyEdits((prev) => ({
      ...prev,
      [repositoryId]: {
        ...getPolicyEdit(policy),
        ...partial,
      },
    }));
  };

  const handleSavePolicy = (policy: MergeGatePolicy) => {
    const edit = getPolicyEdit(policy);
    setSavingPolicyId(policy.repositoryId);
    updatePolicyMutation.mutate({ repositoryId: policy.repositoryId, request: edit });
  };

  const handleRegisterWebhook = () => {
    if (!newWebhookName.trim() || !newWebhookUrl.trim()) return;
    const eventTypes = newWebhookEventTypes
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e.length > 0);
    registerWebhookMutation.mutate({
      name: newWebhookName.trim(),
      url: newWebhookUrl.trim(),
      secret: newWebhookSecret.trim() || undefined,
      eventTypes: eventTypes.length > 0 ? eventTypes : undefined,
    });
  };

  const resetWebhookDialog = () => {
    setWebhookDialogOpen(false);
    setNewWebhookName('');
    setNewWebhookUrl('');
    setNewWebhookEventTypes('');
    setNewWebhookSecret('');
    setCreatedWebhookSecret(null);
  };

  // --- Column Definitions ---

  const prRunColumns: TableColumnDefinition<Run>[] = [
    createTableColumn<Run>({
      columnId: 'status',
      renderHeaderCell: () => 'Status',
      renderCell: (item) => (
        <Badge appearance="filled" color={getRunStatusColor(item.status)}>
          {item.status}
        </Badge>
      ),
    }),
    createTableColumn<Run>({
      columnId: 'name',
      compare: (a, b) => a.name.localeCompare(b.name),
      renderHeaderCell: () => 'PR / Target',
      renderCell: (item) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Body1 style={{ fontWeight: 500 }}>{item.name}</Body1>
          <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
            {item.targetIdentifier}{item.targetBranch ? ` -> ${item.targetBranch}` : ''}
          </Body2>
        </div>
      ),
    }),
    createTableColumn<Run>({
      columnId: 'findings',
      renderHeaderCell: () => 'Findings',
      renderCell: (item) => (
        <div style={{ display: 'flex', gap: tokens.spacingHorizontalS }}>
          {item.criticalFindings > 0 && (
            <Badge appearance="tint" color="danger">{item.criticalFindings} critical</Badge>
          )}
          {item.highFindings > 0 && (
            <Badge appearance="tint" color="danger">{item.highFindings} high</Badge>
          )}
          <Body2>{item.totalFindings} total</Body2>
        </div>
      ),
    }),
    createTableColumn<Run>({
      columnId: 'complianceScore',
      renderHeaderCell: () => 'Compliance Score',
      renderCell: (item) => {
        const score = item.complianceScore ?? 0;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
            <ProgressBar
              value={score / 100}
              thickness="large"
              style={{ width: '80px' }}
            />
            <Body2 style={{ color: getScoreColor(score) }}>{score}%</Body2>
          </div>
        );
      },
    }),
    createTableColumn<Run>({
      columnId: 'date',
      renderHeaderCell: () => 'Date',
      renderCell: (item) => (
        <Body2>{item.startedAt ? new Date(item.startedAt).toLocaleString() : '-'}</Body2>
      ),
    }),
  ];

  const policyColumns: TableColumnDefinition<MergeGatePolicy>[] = [
    createTableColumn<MergeGatePolicy>({
      columnId: 'repository',
      compare: (a, b) => a.repositoryName.localeCompare(b.repositoryName),
      renderHeaderCell: () => 'Repo',
      renderCell: (item) => <Body1 style={{ fontWeight: 500 }}>{item.repositoryName}</Body1>,
    }),
    createTableColumn<MergeGatePolicy>({
      columnId: 'blockOnCritical',
      renderHeaderCell: () => 'Block on Critical',
      renderCell: (item) => {
        const edit = getPolicyEdit(item);
        return (
          <Switch
            checked={edit.blockOnCritical}
            onChange={(_, data) => updatePolicyEdit(item.repositoryId, { blockOnCritical: data.checked }, item)}
          />
        );
      },
    }),
    createTableColumn<MergeGatePolicy>({
      columnId: 'blockOnHigh',
      renderHeaderCell: () => 'Block on High',
      renderCell: (item) => {
        const edit = getPolicyEdit(item);
        return (
          <Switch
            checked={edit.blockOnHigh}
            onChange={(_, data) => updatePolicyEdit(item.repositoryId, { blockOnHigh: data.checked }, item)}
          />
        );
      },
    }),
    createTableColumn<MergeGatePolicy>({
      columnId: 'requireEvidence',
      renderHeaderCell: () => 'Require Evidence',
      renderCell: (item) => {
        const edit = getPolicyEdit(item);
        return (
          <Switch
            checked={edit.requireEvidence}
            onChange={(_, data) => updatePolicyEdit(item.repositoryId, { requireEvidence: data.checked }, item)}
          />
        );
      },
    }),
    createTableColumn<MergeGatePolicy>({
      columnId: 'enabled',
      renderHeaderCell: () => 'Enabled',
      renderCell: (item) => {
        const edit = getPolicyEdit(item);
        return (
          <Switch
            checked={edit.isEnabled}
            onChange={(_, data) => updatePolicyEdit(item.repositoryId, { isEnabled: data.checked }, item)}
          />
        );
      },
    }),
    createTableColumn<MergeGatePolicy>({
      columnId: 'actions',
      renderHeaderCell: () => '',
      renderCell: (item) => (
        <Button
          appearance="primary"
          size="small"
          icon={<SaveRegular />}
          onClick={() => handleSavePolicy(item)}
          disabled={savingPolicyId === item.repositoryId}
        >
          {savingPolicyId === item.repositoryId ? 'Saving...' : 'Save'}
        </Button>
      ),
    }),
  ];

  // --- Webhooks four-state discipline (§13) ---

  const webhookActionButtonCount = canManageWebhooks ? 1 : 0;
  const webhookColumnCount = 6 + webhookActionButtonCount;

  const webhookRegisterDialog = (
    <Dialog
      open={webhookDialogOpen}
      onOpenChange={(_, data) => {
        if (!data.open) {
          resetWebhookDialog();
        } else {
          setWebhookDialogOpen(true);
        }
      }}
    >
      <DialogTrigger disableButtonEnhancement>
        <Button appearance="primary" icon={<AddRegular />}>
          Register Webhook
        </Button>
      </DialogTrigger>
      <DialogSurface style={{ maxWidth: '500px' }}>
        <DialogTitle>Register Webhook</DialogTitle>
        <DialogBody>
          <DialogContent>
            {createdWebhookSecret ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                <MessageBar intent="warning">
                  <MessageBarBody>
                    <MessageBarTitle>Save this secret now</MessageBarTitle>
                    It will not be shown again. Use it to verify the HMAC-SHA256 signature on incoming deliveries.
                  </MessageBarBody>
                </MessageBar>
                <Field label="Webhook Secret">
                  <Input readOnly value={createdWebhookSecret} className={styles.monoText} />
                </Field>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                <Field label="Name" required>
                  <Input
                    placeholder="Production merge gate"
                    value={newWebhookName}
                    onChange={(_, data) => setNewWebhookName(data.value)}
                  />
                </Field>
                <Field label="URL" required>
                  <Input
                    placeholder="https://example.com/webhooks/leftshield"
                    value={newWebhookUrl}
                    onChange={(_, data) => setNewWebhookUrl(data.value)}
                  />
                </Field>
                <Field label="Event Types" hint="Comma-separated. Defaults to validation.completed if left blank.">
                  <Input
                    placeholder="validation.completed, validation.blocked"
                    value={newWebhookEventTypes}
                    onChange={(_, data) => setNewWebhookEventTypes(data.value)}
                  />
                </Field>
                <Field label="Secret" hint="Leave blank to auto-generate an HMAC-SHA256 signing secret.">
                  <Input
                    placeholder="Auto-generated if left blank"
                    value={newWebhookSecret}
                    onChange={(_, data) => setNewWebhookSecret(data.value)}
                  />
                </Field>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            {createdWebhookSecret ? (
              <Button appearance="primary" onClick={resetWebhookDialog}>
                Done
              </Button>
            ) : (
              <>
                <Button appearance="secondary" onClick={resetWebhookDialog}>
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  onClick={handleRegisterWebhook}
                  disabled={!newWebhookName.trim() || !newWebhookUrl.trim() || registerWebhookMutation.isPending}
                >
                  {registerWebhookMutation.isPending ? 'Registering...' : 'Register'}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );

  let webhookListBody: ReactNode;
  if (webhooksIsLoading) {
    webhookListBody = <LoadingSkeleton rows={6} columns={webhookColumnCount} />;
  } else if (webhooksIsError) {
    webhookListBody = <ErrorState message="Unable to load webhooks." onRetry={refetchWebhooks} />;
  } else if (webhookItems.length === 0) {
    webhookListBody = (
      <EmptyState
        icon={<PlugConnectedRegular />}
        title="No Webhooks Registered"
        description="Without webhooks, LeftShield cannot intercept pull requests. Code changes will reach production without governance checks."
        actionLabel={canManageWebhooks ? 'Register Webhook' : undefined}
        onAction={canManageWebhooks ? () => setWebhookDialogOpen(true) : undefined}
      />
    );
  } else {
    webhookListBody = (
      <ListLoadingOverlay busy={webhooksIsFetching && !webhooksIsLoading}>
        <Table aria-label="Registered webhooks">
          <TableHeader>
            <TableRow>
              <TableHeaderCell
                sortable
                sortDirection={
                  webhookSortField === 'name' ? (webhookSortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={webhookSortIcon('name')}
                onClick={() => toggleWebhookSort('name')}
              >
                Name
              </TableHeaderCell>
              <TableHeaderCell style={{ width: '100%' }}>URL</TableHeaderCell>
              <TableHeaderCell>Event Types</TableHeaderCell>
              <TableHeaderCell>Active</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  webhookSortField === 'totalDeliveries' ? (webhookSortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={webhookSortIcon('totalDeliveries')}
                onClick={() => toggleWebhookSort('totalDeliveries')}
              >
                Deliveries
              </TableHeaderCell>
              <TableHeaderCell>Created</TableHeaderCell>
              {canManageWebhooks && (
                <TableHeaderCell style={{ width: actionsColumnWidth(webhookActionButtonCount) }}>
                  Actions
                </TableHeaderCell>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {webhookItems.map((item) => {
              const isDeleting = deleteWebhookMutation.isPending && deleteWebhookMutation.variables === item.id;
              return (
                <TableRow key={item.id}>
                  <TableCell>
                    <Body1 style={{ fontWeight: 500 }}>{item.name}</Body1>
                  </TableCell>
                  <TableCell>
                    <Body2 className={styles.monoText}>{item.url}</Body2>
                  </TableCell>
                  <TableCell>
                    <div className={styles.eventsCell}>
                      {item.eventTypes.map((eventType) => (
                        <Badge key={eventType} appearance="outline" size="small">
                          {eventType}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge appearance="filled" color={item.isActive ? 'success' : 'informative'}>
                      {item.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Body2>{formatDeliveries(item.totalDeliveries, item.failedDeliveries)}</Body2>
                  </TableCell>
                  <TableCell>
                    <Body2>{new Date(item.createdAt).toLocaleDateString()}</Body2>
                  </TableCell>
                  {canManageWebhooks && (
                    <TableCell>
                      {/* Hidden-not-disabled (§18): the Actions column itself is omitted above
                          for non-managers, so this cell only ever renders for a manager. */}
                      <div className={styles.actionsCell}>
                        <Button
                          appearance="subtle"
                          icon={<DeleteRegular />}
                          size="small"
                          onClick={() => deleteWebhookMutation.mutate(item.id)}
                          disabled={isDeleting}
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader title="PR Bot" subtitle="Merge gate enforcement decisions, blocked deployments, and compliance proof" />

      <Card className={styles.card}>
        <div style={{ borderBottom: `1px solid ${tokens.colorNeutralStroke1}`, flexShrink: 0 }}>
          <TabList selectedValue={selectedTab} onTabSelect={handleTabSelect}>
            <Tab value="pullRequests" icon={<BranchRegular />}>
              Pull Requests
            </Tab>
            <Tab value="policies" icon={<ShieldCheckmarkRegular />}>
              Merge Gate Policies
            </Tab>
            <Tab value="webhooks" icon={<PlugConnectedRegular />}>
              Webhooks
            </Tab>
          </TabList>
        </div>

        {/* Pull Requests Tab */}
        {selectedTab === 'pullRequests' && (
          <div style={{ padding: tokens.spacingHorizontalL }}>
            {runsLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXL }}>
                <Spinner size="medium" label="Loading PR scans..." />
              </div>
            ) : prRuns.length > 0 ? (
              <DataGrid items={prRuns} columns={prRunColumns} sortable style={{ minWidth: '800px' }}>
                <DataGridHeader>
                  <DataGridRow>
                    {({ renderHeaderCell }) => (
                      <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                    )}
                  </DataGridRow>
                </DataGridHeader>
                <DataGridBody<Run>>
                  {({ item, rowId }) => (
                    <DataGridRow<Run> key={rowId}>
                      {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                    </DataGridRow>
                  )}
                </DataGridBody>
              </DataGrid>
            ) : (
              <Body1 className={ps.emptyState}>
                No PR enforcement activity. When configured, LeftShield intercepts every pull request — evaluating policies, blocking risky changes, and generating proof. Without this, risky code can reach production undetected. Configure webhooks to start.
              </Body1>
            )}
          </div>
        )}

        {/* Merge Gate Policies Tab */}
        {selectedTab === 'policies' && (
          <div style={{ padding: tokens.spacingHorizontalL }}>
            {policiesLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXL }}>
                <Spinner size="medium" label="Loading policies..." />
              </div>
            ) : policies && policies.length > 0 ? (
              <DataGrid items={policies} columns={policyColumns} sortable style={{ minWidth: '900px' }}>
                <DataGridHeader>
                  <DataGridRow>
                    {({ renderHeaderCell }) => (
                      <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                    )}
                  </DataGridRow>
                </DataGridHeader>
                <DataGridBody<MergeGatePolicy>>
                  {({ item, rowId }) => (
                    <DataGridRow<MergeGatePolicy> key={rowId}>
                      {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                    </DataGridRow>
                  )}
                </DataGridBody>
              </DataGrid>
            ) : (
              <Body1 className={ps.emptyState}>
                No merge gate policies configured. Without merge gate enforcement, all code changes bypass governance. Register webhooks to activate protection.
              </Body1>
            )}
          </div>
        )}

        {/* Webhooks Tab — migrated onto the List & Grid Standard local primitives. */}
        {selectedTab === 'webhooks' && (
          <div className={styles.webhooksPanel}>
            <ListPageLayout
              header={<MasterDetailHeader crumbs={webhookCrumbs} busy={webhooksIsFetching && !webhooksIsLoading} />}
              toolbar={
                <ListToolbar
                  primaryActions={canManageWebhooks ? webhookRegisterDialog : undefined}
                  search={webhookListState.search}
                  onSearchChange={setWebhookSearch}
                  searchPlaceholder="Search webhooks…"
                  onRefresh={refetchWebhooks}
                  isRefreshing={webhooksIsFetching}
                  testIdPrefix="pr-bot-webhooks"
                />
              }
              footer={
                <Pager
                  page={webhookListState.page}
                  totalPages={webhookTotalPages}
                  hasPreviousPage={webhookHasPreviousPage}
                  hasNextPage={webhookHasNextPage}
                  totalCount={webhookTotalCount}
                  onPageChange={setWebhookPage}
                  testId="pr-bot-webhooks-pager"
                />
              }
            >
              {webhookListBody}
            </ListPageLayout>
          </div>
        )}
      </Card>
    </div>
  );
};
